import { faArrowAltCircleRight } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Card, CardActionArea, CardActions, CssBaseline, Divider, Drawer, Hidden, Link, ListItem, ListItemText, Stepper as MUIStepper, Step, StepLabel, Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import { motion } from "framer-motion";
import { DRAWER_WIDTH } from 'pages/CustomerBooking/Stepper/Sidebar';
import { Wizard } from 'pages/CustomerBooking/Stepper/Wizard';
import { useCallback, useMemo, useState } from 'react';
import ReactJson from 'react-json-view';
import { useHistory, useLocation } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { isAuthenticated, useUserState } from 'services/authentication.service';
import { useTrackEvent } from 'util/trackEvent';
import { slideRight, transition } from 'util/transitions';
import { BREADCRUMBS, CREATEACCOUNTCHAINSTEPS, STEP_BOOKING_LIST, STEP_SIGN_IN } from './constants';
import { useCreateBooking } from './createHook';
import { bookingRegionState, bookingState, cartVisibleState, getBreadcrumbState, invalidCartState, isCartOpenState, packagesState, prefillCartState, servicesState, stepState, timeSlotState } from './state';

const useStyles = makeStyles(theme => ({
  sidebarRoot: {
    display: 'flex',
    height: '100%',
    width: '100%',
    backgroundColor: theme.palette.grey[100],
  },
  drawer: {
    [theme.breakpoints.up('md')]: {
      width: DRAWER_WIDTH,
      flexShrink: 0,
    },
  },
  appBar: {
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${DRAWER_WIDTH}px)`,
      marginLeft: DRAWER_WIDTH,
    },
  },
  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  // necessary for content to be below app bar
  toolbar: theme.mixins.toolbar,
  drawerPaper: {
    display: 'flex',

    width: DRAWER_WIDTH,
  },
  content: {
    [theme.breakpoints.down('sm')]: {
      marginLeft: DRAWER_WIDTH,
    },
    overflowX: 'hidden',
    display: 'flex',
    flex: '1 1 auto',
    width: '100%',
    paddingLeft: theme.spacing(6),
    paddingRight: theme.spacing(6),
    backgroundColor: theme.palette.grey[100],
    scrollbarGutter: "stable both-edges",
  },
  padded: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  logout: {
    bottom: 0
  },
  stepper: {
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 0,
      paddingRight: 0
    },
    cursor: 'pointer',
  },
  action: {
    flexGrow: .5,
    textAlign: 'center',
    '&>p': {
      paddingTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    }
  }
}));

export const StepComponent = ({ step }) => {
  const classes = useStyles();
  const [_step, setStep] = useRecoilState(stepState);
  const [stepIndex, setStopIndex] = useState(0);
  const breadcrumbState = useRecoilValue(getBreadcrumbState)

  const [booking, setBooking] = useRecoilState(bookingState);
  const trackEvent = useTrackEvent()
  const history = useHistory()
  const linkifySteps = useCallback(
    (crumbs) => {
      return crumbs?.map((s) => {
        if (breadcrumbState.includes(s.value)) {
          return { ...s, link: true }
        } else {
          return { ...s, link: false }
        }
      })
    },
    [],
  );
  const crumbs = useMemo(
    () => {
      const stopIndex = BREADCRUMBS.findIndex((s) => {
        return s?.value === step
      })

      const preCrumbs = BREADCRUMBS.slice(0, stopIndex)?.map((s) => ({ ...s, link: true }))

      const postCrumbs = stopIndex < 0 ? [] : linkifySteps(BREADCRUMBS.slice(stopIndex + 1))
      let filteredSidebarItems = [...preCrumbs, { ...BREADCRUMBS[stopIndex], active: true, link: true }, ...postCrumbs].filter((s) => s.sidebar)
      if (isAuthenticated()) {
        filteredSidebarItems = filteredSidebarItems.filter((s) => !s?.hideOnValidate)
      } else {
        if (_step === STEP_SIGN_IN ) {
          filteredSidebarItems = filteredSidebarItems.filter((s) => !s?.createAccountOnly)
        } if (CREATEACCOUNTCHAINSTEPS.includes(_step)) {
          filteredSidebarItems = filteredSidebarItems.filter((s) => !s?.hideIfCreateAccount)
        }
      }
      
      setStopIndex(filteredSidebarItems.findIndex((s) => {
        return s?.value === step
      }))

      return filteredSidebarItems

    }, [_step, step, BREADCRUMBS, bookingState, breadcrumbState]
  );

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("xs"),
    {
      noSsr: true,
    });

  return (
    <MUIStepper
      className={classes.stepper}
      activeStep={stepIndex}
      orientation={isMobile ? "horizontal" : "vertical"}
      alternativeLabel={isMobile}
    >
      {crumbs.map((crumb) => (
        <Step key={crumb?.value}>
          <StepLabel>
            {crumb?.link ? (
              <Link onClick={() => {
                trackEvent(`Sidebar click link ${crumb?.label}}`)
                setStep(crumb?.value)
                history.replace(crumb?.value, { internal: true })
              }}>
                {crumb?.active ?
                  (<b>{crumb?.label}</b>) : (<>{crumb?.label}</>)
                }
              </Link>
            ) : (
              <Typography variant="body2">
                {crumb?.active ?
                  (<b>{crumb?.label}</b>) : (<>{crumb?.label}</>)
                }
              </Typography>
            )}
          </StepLabel>
        </Step>
      ))}
    </MUIStepper>
  )


}
const SideBar = ({ window, children }) => {
  const classes = useStyles()
  const trackEvent = useTrackEvent()
  const [booking, setBooking] = useRecoilState(bookingState)
  const [step, setStep] = useRecoilState(stepState);
  const [isCartOpen, setIsCartOpen] = useRecoilState(isCartOpenState);
  const [packages, setPackages] = useRecoilState(packagesState);
  const [services, setServices] = useRecoilState(servicesState);
  const [invalidCart, setInvalidCart] = useRecoilState(invalidCartState);
  const [timeslot, setTimeslot] = useRecoilState(timeSlotState);
  const [enableCart, setEnableCart] = useRecoilState(cartVisibleState);
  const [prefillCart, setPrefillCart] = useRecoilState(prefillCartState);
  const [selectedRegion, setSelectedRegion] = useRecoilState(bookingRegionState);
  const location = useLocation()
  const history = useHistory()
  const { clearState } = useCreateBooking()
  const sidebarlocal = process.env.REACT_APP_ENVIRONMENT === 'local'
  const enableMyBookingsReroute = true
  const { getIdentity, user, getUser, logout } = useUserState(clearState)
  return (
    <div className={classes.sidebarRoot}>
      <CssBaseline />
      <nav className={classes.drawer}>
        <Hidden xsDown implementation="js">
          <Drawer
            classes={{
              paper: classes.drawerPaper,
            }}
            variant='permanent'
            open
          >
            <Wizard
              isCreate
              isMobile={false}
              onLogout={() => {
                trackEvent('Sidebar click Logout')
                logout()
              }}
              StepComponent={<StepComponent step={step} />}
            />
            <Divider />
            {isAuthenticated() ? (
              <Box flexGrow={.5}>
                <Card>
                  {enableMyBookingsReroute ? (
                    <CardActionArea
                      onClick={() => {
                        trackEvent('Sidebar click my bookings')
                        setStep(STEP_BOOKING_LIST)
                      }}
                    >
                      <ListItem>
                        <ListItemText
                          primary="My Bookings"
                          secondary="Go to your existing and booking history"
                        />

                        <FontAwesomeIcon icon={faArrowAltCircleRight} />

                      </ListItem>
                    </CardActionArea>
                  ):<></>}
                  <CardActions>

                    <Button
                      fullWidth
                      className={classes.logout}
                      variant='outlined'
                      onClick={() => {
                        trackEvent('Sidebar click Logout')
                        logout()
                      }
                      }>Logout</Button>
                  </CardActions>
                </Card>

              </Box>
            ) : (
              <div className={classes.action}>
                <Typography variant="body2">Have a Zennya account?</Typography>
                <Button
                  onClick={() => {
                    trackEvent('Sidebar click Login')
                    history.replace(STEP_SIGN_IN, { internal: true })
                  }}
                  variant="outlined"
                  color="primary"
                  fullWidth
                >
                  Login
                </Button>
              </div>
            )}

            {sidebarlocal && (
              <ReactJson
                collapsed={1}
                indentWidth={2}
                sortKeys
                displayDataTypes={false}
                displayObjectSize={false}
                src={{ cart: { prefillCart, isCartOpen, enableCart, invalidCart, services, packages }, booking, selectedRegion, timeslot, step  }} />
            )}
          </Drawer>
        </Hidden>
      </nav>
      <motion.div id="zi-zwb-createbooking-content" {...slideRight} transition={transition} key={location.pathname} className={classes.content}>
        {children}
      </motion.div>
    </div>
  )
}

export { SideBar };
